/**
 * /cookies — a PAGE. AppLayoutRoute supplies the chrome.
 *
 * Unlike privacy/terms/refund/cancellation, this carries REAL policy rather
 * than prompts, so `draft` is false and the placeholder banner stays off.
 * Saying "this is placeholder text" above a written policy would be worse than
 * saying nothing.
 */
import { Link } from 'react-router-dom';
import { LegalPage } from '../components/LegalPage';

export default function CookiesPage() {
  return (
    <LegalPage
      draft={false}
      title="Cookie Policy"
      description="How Bloomneo uses cookies and similar technologies. We keep it minimal — no third-party advertising trackers."
      updated="January 2026"
      intro="We try to keep our use of cookies to the bare minimum. We don't use third-party advertising trackers, we don't profile you across the web, and we don't sell your data to anyone. This Cookie Policy explains what we do use, why we use it, and how you can control it."
      sections={[
        {
          heading: 'What are cookies?',
          body: (
            <>
              <p>
                Cookies are small text files that websites store on your device when you visit them. They're used to remember information about your visit — like your preferences, login state, or what you clicked.
              </p>
              <p>
                Cookies aren't bad by themselves. They're a fundamental part of how the modern web works. The problem is when they're used to track people across hundreds of sites and build invasive advertising profiles. We don't do that.
              </p>
            </>
          ),
        },
        {
          heading: 'What we use cookies for',
          body: (
            <>
              <p>We use cookies for three things only:</p>
              <ul>
                <li><strong>Essential functionality</strong>: keeping you logged in, remembering your dashboard preferences, securing your session.</li>
                <li><strong>Analytics</strong>: understanding which pages people visit so we can improve them. We use privacy-respecting analytics — no Google Analytics, no Facebook Pixel.</li>
                <li><strong>Theme &amp; preferences</strong>: remembering whether you prefer light or dark mode.</li>
              </ul>
              <p>
                That's it. No advertising cookies. No third-party trackers. No selling your behavior to data brokers.
              </p>
            </>
          ),
        },
        {
          heading: 'Types of cookies we use',
          body: (
            <>
              <p><strong>Strictly necessary cookies</strong></p>
              <p>
                These are required for our website to work. They handle things like authentication, security, and load balancing. You can't disable these because the site wouldn't function without them.
              </p>
              <p><strong>Functional cookies</strong></p>
              <p>
                These remember your preferences — theme (light/dark mode), language, and dashboard layout. They make your experience better but aren't strictly necessary.
              </p>
              <p><strong>Analytics cookies</strong></p>
              <p>
                These help us understand how visitors use our website so we can improve it. We use privacy-respecting analytics tools that don't track individuals or share data with third parties. The data is aggregated and anonymized.
              </p>
            </>
          ),
        },
        {
          heading: 'Third-party cookies',
          body: (
            <>
              <p>
                We try to minimize third-party cookies. The few we use are from trusted service providers that help us operate:
              </p>
              <ul>
                <li><strong>Payment processors</strong> (when you make a purchase) — required to process transactions securely</li>
                <li><strong>Customer support tools</strong> (when you use our chat) — to maintain your support session</li>
              </ul>
              <p>
                We do <strong>not</strong> use:
              </p>
              <ul>
                <li>Google Analytics or any Google tracking</li>
                <li>Facebook Pixel or any Meta tracking</li>
                <li>Advertising network cookies</li>
                <li>Cross-site tracking pixels</li>
                <li>Behavioral profiling tools</li>
              </ul>
            </>
          ),
        },
        {
          heading: 'How to control cookies',
          body: (
            <>
              <p>
                You have full control over cookies through your browser settings. You can:
              </p>
              <ul>
                <li>Block all cookies (note: this may break essential functionality)</li>
                <li>Block only third-party cookies</li>
                <li>Delete existing cookies</li>
                <li>Set preferences for individual websites</li>
              </ul>
              <p>
                Browser-specific instructions:
              </p>
              <ul>
                <li><strong>Chrome</strong>: Settings → Privacy and security → Cookies and other site data</li>
                <li><strong>Firefox</strong>: Settings → Privacy &amp; Security → Cookies and Site Data</li>
                <li><strong>Safari</strong>: Preferences → Privacy → Cookies and website data</li>
                <li><strong>Edge</strong>: Settings → Cookies and site permissions → Cookies and site data</li>
              </ul>
              <p>
                Disabling cookies may prevent certain features from working correctly — for example, you may need to log in every visit, and dashboard preferences won't be remembered.
              </p>
            </>
          ),
        },
        {
          heading: 'Do Not Track',
          body: (
            <p>
              Some browsers send a "Do Not Track" signal to websites. We respect this signal and disable our analytics cookies for users who have enabled DNT in their browser.
            </p>
          ),
        },
        {
          heading: 'Cookies on our customer applications',
          body: (
            <>
              <p>
                When we build custom applications for our clients, those applications may use cookies based on the client's specific requirements. The cookie policy for those applications is determined by the client, not by us.
              </p>
              <p>
                For example, if we build a customer portal for a clinic network, the cookies used in that portal are governed by the clinic's privacy policy, not Bloomneo's.
              </p>
            </>
          ),
        },
        {
          heading: 'Changes to this policy',
          body: (
            <p>
              We may update this Cookie Policy if we change how we use cookies. When we do, we'll update the "Last reviewed" date at the top of this page. For significant changes, we'll notify you by email or through the dashboard.
            </p>
          ),
        },
        {
          heading: 'More information',
          body: (
            <>
              <p>
                For more about how we handle data in general, see our <Link to="/privacy">Privacy Policy</Link>. For terms of use, see our <Link to="/terms">Terms of Service</Link>.
              </p>
              <p>
                Questions about cookies? <Link to="/contact">Contact us</Link> — we'll answer in plain language.
              </p>
            </>
          ),
        },
      ]}
    />
  );
}
